import { trpc } from '@tencent/tpro_rmonitor_web_server_bitmap';
import { get, invert, isArray, isBoolean, isEmpty, isNil, isNumber, mapValues, pickBy, isFinite } from 'lodash-es';
import moment from 'moment';
import i18n from '@/i18n';
import {
  getProductId,
  isAndroid,
  isFlutter,
  isHarmonyOS,
  isIOS,
  isMac,
  isWindows,
  isVisionOS,
  isAppleTV,
} from '@/infrastructure/product-info';
import { CUSTOM_ACTIONS } from '@/infrastructure/reporter/datong/constant';
import { reportClick } from '@/infrastructure/reporter/datong/report';
import { storageGet, storageSet } from '@/infrastructure/storage-utils';
import { CommonDateRange, getDateRangeByShortCut } from '@/infrastructure/utils/timestamp';
import { toArray } from '@/infrastructure/utils/to-array';
import { DeviceType } from '@/presentation/components/dashboard-header';
import {
  ANDROID_EXCEPTION_TYPE_LIST_OPTIONS,
  ANR_EXCEPTION_TYPE_LIST_OPTIONS,
  DEFAULT_EXCEPTION_TYPE_LIST_IOS,
  ERROR_EXCEPTION_TYPE_LIST_OPTIONS,
  ERROR_FLUTTER_EXCEPTION_TYPE_LIST_OPTIONS, HARMONY_EXCEPTION_TYPE_LIST_OPTIONS,
  IOS_EXCEPTION_TYPE_LIST_OPTIONS,
  REPORT_ISSUE_FORM_LABEL,
} from '@/presentation/components/exception-form/types';
import { DETAIL_SELECT_OPTION_STORAGE_NAME } from '@/presentation/components/form/components/detail-select-group';
import { NumberType } from '@/presentation/components/form/components/select-number';
import { DEFAULT_BUGLY_LIMIT_DAYS } from '@/presentation/components/form/components/time-range';
import {
  BUGLY_DATE_RANGE_BY_SHORT_CUT,
  shortRanges,
} from '@/presentation/components/form/components/time-range-cascade';
import { FieldsOperator, timeQuickSetting } from '@/presentation/components/form/proto';
import { MatchMode } from '@/presentation/components/form/standards/types';
import { AreaNameEnum } from '@/presentation/components/tds-form/tds-area';
import { TagInfo } from '@/presentation/pages/setting/tag-manage/api';
import { IssueFilterCompareParams, IssueFilterParams } from '@/presentation/store/issue';
import { AppVersionOperator, DetailModeOperator, DetailOperator, FIELD_ALL, FieldKey } from '@/proto/field';
import { IssueType } from '@/proto/issue';
import { IFilter, TimeType } from '@/proto/metric';
import {
  appVersionField2Filter as newAppVersionField2Filter,
  filterTransformMap,
  getFilterKeyForCustomTag,
  isValidFilter,
  KeyDataType,
} from '@/proto/token';

import { WithTypeCast } from '@raft/utils';
import { toHumpString } from '@raft/utils/src/case/camel-case';
import { toLineString } from '@raft/utils/src/case/snake-case';
type IBizExtendInfo = WithTypeCast<trpc.rmonitor.web_server.IBizExtendInfo>;

// 定义选项类型
type ExceptionTypeOption = {
  label: string;
  value: string;
};

/**
 * 获取Issue类型列表的下拉配置项
 */
export const getExceptionTypeOptions = (issueType: IssueType): ExceptionTypeOption[] => {
  if (isFlutter()) {
    return ERROR_FLUTTER_EXCEPTION_TYPE_LIST_OPTIONS;
  }

  let exceptionTypeOptions: ExceptionTypeOption[];
  switch (issueType) {
    case IssueType.ISSUE_TYPE_ERROR:
      exceptionTypeOptions = ERROR_EXCEPTION_TYPE_LIST_OPTIONS;
      break;
    case IssueType.ISSUE_TYPE_CRASH:
    case IssueType.ISSUE_TYPE_FOOM_BUGLY:
      if (isAndroid()) {
        exceptionTypeOptions = ANDROID_EXCEPTION_TYPE_LIST_OPTIONS;
      } else if (isIOS() || isVisionOS() || isAppleTV()) {
        exceptionTypeOptions = IOS_EXCEPTION_TYPE_LIST_OPTIONS;
      } else if (isMac()) {
        exceptionTypeOptions = [
          { label: DEFAULT_EXCEPTION_TYPE_LIST_IOS, value: DEFAULT_EXCEPTION_TYPE_LIST_IOS },
        ];
      } else if (isWindows()) {
        exceptionTypeOptions = ANDROID_EXCEPTION_TYPE_LIST_OPTIONS;
      } else if (isHarmonyOS()) {
        exceptionTypeOptions = HARMONY_EXCEPTION_TYPE_LIST_OPTIONS;
      } else {
        exceptionTypeOptions = ANDROID_EXCEPTION_TYPE_LIST_OPTIONS;
      }
      break;
    case IssueType.ISSUE_TYPE_ANR_BUGLY:
      exceptionTypeOptions = ANR_EXCEPTION_TYPE_LIST_OPTIONS;
      break;
    case IssueType.ISSUE_TYPE_ELECTRON_CRASH:
      // 对于Electron崩溃，返回空数组，避免生成type过滤器
      exceptionTypeOptions = [];
      break;
    default:
      exceptionTypeOptions = ANDROID_EXCEPTION_TYPE_LIST_OPTIONS;
      break;
  }

  return exceptionTypeOptions;
};

const isValidValue = (value: string | string[]) => {
  if (isArray(value)) {
    return value.filter(item => !!item).length > 0;
  }
  return value !== FIELD_ALL;
};

export const clearFilters = (issueFilters: IssueFilterParams) => {
  const filterKeys = (Object.keys(issueFilters) as Array<keyof IssueFilterParams>);
  return filterKeys.reduce((prev: IssueFilterParams, currentKey) => {
    const currentValue = issueFilters[currentKey];
    const returnedValue = { ...prev };
    if (currentValue && isValidValue(currentValue)) {
      returnedValue[currentKey] = currentValue;
    }

    return returnedValue;
  }, {});
};

export interface FilterType {
  name: string;
  values: string[];
  operator: string;
}

/**
 * 查询参数转换, 自定义字段处理、不同类型字段参数处理
 */
export function formatSearchParams(
  searchParams: IssueFilterParams,
  type: IssueType,
  tagList?: TagInfo[],
) {
  const optionalFilters: FilterType[] = [];
  const params = clearFilters(searchParams);
  const { customDate, timeQuick } = params;
  const dateFormat = 'YYYY-MM-DD HH:mm:ss';
  const isDateRangeByShortCut = BUGLY_DATE_RANGE_BY_SHORT_CUT.includes(timeQuick ?? '');

  if (customDate) {
    const dateRange = getDateRangeByShortCut(customDate);
    params.startDateStr = dateRange[0].format(dateFormat);
    params.endDateStr = dateRange[1].format(dateFormat);
  }

  if (isDateRangeByShortCut) {
    const dateRange = getDateRangeByShortCut(timeQuick ?? '', shortRanges);
    params.startDateStr = dateRange[0].format(dateFormat);
    params.endDateStr = dateRange[1].format(dateFormat);
  }

  const userTagInfos: Record<string, string>[] = [];

  if (userTagInfos.length) {
    params.userTagInfos = JSON.stringify(userTagInfos);
  }

  // 处理参数转换, urldecode
  if (params.tagList && type === IssueType.ISSUE_TYPE_CRASH) {
    delete params.tagList;
  }

  if (params.appVersionOperator) {
    optionalFilters.push(appVersionField2Filter(params, false, FieldKey.AppVersion));
  }

  if (params.channelId) {
    params.channelId = decodeURIComponent(params.channelId);
  }

  if (params.bundleId) {
    params.bundleId = decodeURIComponent(params.bundleId);
  }

  if (params.processor && type !== IssueType.ISSUE_TYPE_CRASH) {
    params.processor = decodeURIComponent(params.processor);
  }

  if (params.platform && params.osVersion) {
    delete params.platform;
  }

  if (params.platform) {
    params.osVersion = decodeURIComponent(params.platform);
    delete params.platform;
  }

  if (isArray(searchParams.newTagList)
    && (searchParams.newTagList?.filter(item => !!item)?.length === 0 || !searchParams.newTagList)
        && searchParams.tagColor) {
    params.newTagList = tagList?.filter(item => item.tagColor === searchParams.tagColor)
      ?.map(item => item?.tagId ?? '');
  }

  if (Array.isArray(searchParams.detail) && searchParams.detail.length) {
    params.detail = detailRule(searchParams);
  }
  if (!Array.isArray(params.freeMem) && typeof(params.freeMem) === 'object' && params.freeMem?.value?.length) {
    optionalFilters.push(...SelectNumberFilter(params.freeMem, filterTransformMap.freeMem));
  }
  if (!Array.isArray(params.launchDuration) && typeof(params.launchDuration) === 'object' && params.launchDuration?.value?.length) {
    optionalFilters.push(...SelectNumberFilter(params.launchDuration, filterTransformMap.launchDuration));
  }

  if (optionalFilters.length) {
    const newOptionalFilters = optionalFilters.filter(item => !isEmpty(item?.values) && item?.values.every(v => v !== '' && !isEmpty(v)));
    params.optionalFilters = JSON.stringify(newOptionalFilters);
  }
  if (params?.sceneDataLabels?.length) {
    params.sceneDataLabels = JSON.stringify(params.sceneDataLabels);
  }
  if (params?.linkages?.length) {
    params.linkages = JSON.stringify(params.linkages);
  }
  delete params.launchDuration;
  delete params.freeMem;
  delete params.tagColor;
  delete params.appIsFlutter;
  delete params.accurateDate;
  delete params.appVersionOperator;
  delete params.timeQuick;
  delete params.timeWindow;
  delete params.version;
  delete params.accurateDate;
  delete params.accurateDateCompare;
  delete params.filters;
  delete params.detailMode;
  delete params.isParticiple;
  return params;
}

/**
 * 出错堆栈参数规则
 */
export const detailRule = (searchParams: IssueFilterParams): string | undefined => {
  if (Array.isArray(searchParams?.stack?.values) && searchParams?.stack?.values.length) {
    if (searchParams.isParticiple === DetailOperator.On) {
      switch (searchParams.detailMode) {
        case DetailModeOperator.MatchPhraseUnion:
          return  searchParams?.stack?.values.join('.');
        case DetailModeOperator.MatchPhraseJoin:
          return `"${searchParams?.stack?.values.join('.')}"`;
        case DetailModeOperator.FuzzyNatch:
          return `*${searchParams?.stack?.values[0]}*`;
        default:
          return undefined;
      }
    } else {
      return `${searchParams.detailMode || ''} ${searchParams?.stack?.values?.join('|')}`;
    }
  } else {
    return undefined;
  }
};


/**
 * 出错堆栈filter参数规则
 */
export const detailFilterRule = (
  searchParams: IssueFilterParams,
  transformKey: string,
  filters: { name: string; values: string[]; operator: string }[],
) => {
  if (Array.isArray(searchParams?.stack?.values) && searchParams?.stack?.values?.length) {
    filters.push({
      name: toLineString(transformKey),
      values: searchParams.stack.values,
      operator: searchParams.stack.mode,
    });
  }
};

export const SelectNumberFilter = (
  value: NumberType,
  transformKey: string,
  isToken?: boolean,
) => {
  const values = value.value.map((item) => {
    if (isNil(item)) {
      return '';
    }
    if (isNaN(item)) {
      return '';
    }
    if (transformKey === 'freeMem') {
      return String(Math.round(item * 1024 * 1024 * 1024));
    }
    return String(item);
  });
  // token 需要用逗号分割
  if (isToken && values?.length > 1) {
    return [{
      name: toLineString(transformKey),
      values: [values.join(',')],
      operator: value.operator,
    }];
  }
  return [{
    name: toLineString(transformKey),
    values,
    operator: value.operator,
  }];
};


/**
 * 存储出错堆栈搜索项
 */
export const detailLocalStorage = (paramsDetail: string[]) => {
  if (!paramsDetail?.length) return;
  let detailLocalStorageData = [];

  try {
    const list = JSON.parse(storageGet(DETAIL_SELECT_OPTION_STORAGE_NAME) || '[]');
    if (Array.isArray(list)) {
      detailLocalStorageData = list;
    }
  } catch (e) {

  }

  const detail = [...new Set([...paramsDetail, ...(detailLocalStorageData?.filter?.(str => str && typeof(str) === 'string') || [])])];
  if (detail.length > 20) {
    detail.splice(0 - (detail.length - 20), detail.length - 20);
  }
  storageSet(DETAIL_SELECT_OPTION_STORAGE_NAME, JSON.stringify(detail));
};

/**
 * 判断标签和颜色的组合关系是否满足条件
 */
export const isEveryTagColor = async (searchParams: IssueFilterParams, tagList: TagInfo[]): Promise<boolean> => {
  if (searchParams.tagColor?.length && searchParams.tagColor[0] && !tagList.find(item => item.tagColor
    && searchParams.tagColor?.includes(item.tagColor))) {
    return false;
  }
  if (isArray(searchParams.newTagList)
    && searchParams.newTagList?.filter(item => !!item)?.length
    && searchParams.tagColor?.length && searchParams.tagColor[0]) {
    return searchParams.newTagList?.map(item => tagList.find(tag => tag.tagId === item)?.tagColor)
      ?.every(item => item && searchParams.tagColor?.includes(item));
  }
  return true;
};

export enum BuglyFiltersName {
  Stage = 'stage',
  Brand = 'brand',
  OOMType = 'oom_type',
  TagColor = 'tag_color',
  FreeMem = 'free_mem',
  LaunchDuration = 'launch_duration',
  ExceptionType = 'exception_type',
  StartTime = 'start_time',
  EndTime = 'end_time',
  StartTimeCompare = 'start_time_compare',
  EndTimeCompare = 'end_time_compare',
  Platform = 'platform',
  ProcessName = 'process_name',
  SdkVersion = 'sdk_version',
  Model = 'model',
  KeyMethod = 'key_method',
  CrashId = 'crash_id',
  IssueUuid = 'issue_uuid',
  AppVersion = 'app_version',
  AppVersionCompare = 'app_version_compare',
  BundleId = 'bundle_id',
  ChannelId = 'channel_id',
  CpuArc = 'cpu_arc',
  AppInBack = 'app_in_back',
  Os = 'os',
  AccountId = 'account_id',
  DeviceId = 'device_id',
  DeviceType = 'device_type',
  Stack = 'stack',
  ThreadName = 'thread_name',
  IsROOT = 'is_ROOT',
  RomName = 'rom_name',
  CpuArchitecture = 'cpu_architecture',
  ElectronCpuArch = 'arch',
  IssueStatus = 'issue_status',
  TapdStatus = 'tapd_status',
  Linkages = 'linkages',
  TapdHandler = 'tapd_handler',
  TagList = 'tag_list',
  Type = 'type',
  // 实验ID
  ShiplyTag = 'shiplyTag',
  // 实验标签
  AbTestTag = 'abTestTag',

  // 个例标签
  TagsInFilter =  'tagsInFilter',

  // 异常现场
  SceneLabels = 'scene_labels',

  // 附件
  files = 'files'
}


export const appVersionField2Filter = (
  fields: IssueFilterCompareParams,
  isCompare?: boolean,
  name?: string,
): { name: string; values: string[]; operator: string } => {
  const operator = isCompare ? fields.appVersionOperatorCompare : fields.appVersionOperator;
  const minAppVersion = isCompare ? fields.minAppVersionCompare : fields.minAppVersion;
  const maxAppVersion = isCompare ? fields.maxAppVersionCompare : fields.maxAppVersion;
  const wildcardAppVersion = isCompare ? fields.wildcardAppVersionCompare : fields.wildcardAppVersion;
  const appVersion = isCompare ? fields.versionCompare : fields.version;
  if (operator === AppVersionOperator.Between) {
    const values = (minAppVersion && maxAppVersion) ? `${minAppVersion},${maxAppVersion}` : '';
    return {
      name: name || BuglyFiltersName.AppVersion,
      values: toArray(values),
      operator,
    };
  }
  if (operator === AppVersionOperator.Wildcard) {
    const values = wildcardAppVersion?.replaceAll('*', '') ?? '';
    const operator = getWildcardOperator(wildcardAppVersion ?? '', false);

    return {
      name: name || BuglyFiltersName.AppVersion,
      values: toArray(values!),
      operator,
    };
  }

  const values = toArray(appVersion!);

  return {
    name: name || BuglyFiltersName.AppVersion,
    values,
    operator: operator || '',
  };
};

export const getWildcardOperator = (value: string, hasMatch = true) => {
  let operator = '';
  const wildcardSymbol = '*';
  if (value.startsWith(wildcardSymbol) && value.endsWith(wildcardSymbol) && hasMatch) {
    operator = 'MATCH';   // 包含/匹配
  } else if (value.startsWith(wildcardSymbol) && value.endsWith(wildcardSymbol) && !hasMatch) {
    operator = FieldsOperator.MIDMATCH;   // 中间匹配
  } else if (value.startsWith(wildcardSymbol)) {
    operator = 'SUFFIX';  // 后缀匹配
  } else if (value.endsWith(wildcardSymbol)) {
    operator = 'PREFIX';  // 前缀匹配
  }
  return operator;
};

export const replaceWildcardValue = (value: string) => value.replaceAll('*', '').trim();

export const parseWildcardValue = (value: string, operator: string) => {
  switch (operator) {
    case 'MATCH':
      return `*${value}*`;
    case 'SUFFIX':
      return `*${value}`;
    case 'PREFIX':
      return `${value}*`;
    default:
      return value;
  }
};

export const exceptionTypeIds: Record<string, string[]> = {
  Crash: ['100', '200'],
  ExtensionCrash: ['105', '205'],
  Native: ['101', '201'],
  AllCatched: ['102', '202'],
  Unity3D: ['104', '204'],
  Lua: ['106', '206'],
  JS: ['107', '207'],
  Dart: ['109', '209'],
};

export const getExceptionTypeIds = (type: string) => {
  // 如果type为空、undefined或null，返回空数组
  if (!type || type === 'undefined' || type === 'null') return [];
  if (type === 'ANR') return '';
  const typeArr = type.split(',');
  return typeArr.reduce((previousValue: string[], currentValue) => {
    const typeIds = exceptionTypeIds[currentValue];
    // 如果找不到对应的typeIds，跳过这个值
    if (!typeIds) return previousValue;
    return previousValue.concat(typeIds);
  }, []);
};

export const APP_IN_BACK_TRANSFORM: Record<string, string> = {
  0: '',
  3: 'false',
  2: 'true',
};

/**
 * options: [
 *       { label: '所有', value: '0' },
 *       { label: '前台', value: '3' },
 *       { label: '后台', value: '2' },
 *     ] 转换
 * @param val
 */
export const getAppInBack = (val: string) => APP_IN_BACK_TRANSFORM[val] || '';

/**
 * 质量operator转性能
 */
export const transformOperator = (operator: FieldsOperator) => {
  const operatorMap: any = {
    [FieldsOperator.Is]: FieldsOperator.OR,
    [FieldsOperator.ISNOT]: FieldsOperator.NOT,
    [FieldsOperator.CONTAIN]: FieldsOperator.MATCH,
    [FieldsOperator.NOTCONTAIN]: FieldsOperator.NOTMATCH,
  };

  return operatorMap[operator] || operator;
};
/**
 * 性能operator转质量
 */
export const qualityOperator = (operator: FieldsOperator) => {
  const operatorMap: any = {
    [FieldsOperator.OR]: FieldsOperator.Is,
    [FieldsOperator.NOT]: FieldsOperator.ISNOT,
    [FieldsOperator.MATCH]: FieldsOperator.CONTAIN,
    [FieldsOperator.NOTMATCH]: FieldsOperator.NOTCONTAIN,
  };

  return operatorMap[operator] || operator;
};


export const getIssueListFilters = (params: any) => {
  const { customDate } = params;
  let timeRange: string[] = [];

  if (customDate) {
    const dateRange = getDateRangeByShortCut(customDate);
    timeRange = [
      dateRange[0].unix().toString(),
      dateRange[1].unix().toString(),
    ];
  } else if (params.startDateStr && params.endDateStr) {
    timeRange = [
      moment(params.startDateStr).unix()
        .toString(),
      moment(params.endDateStr).unix()
        .toString(),
    ];
  } else if (params.startTime && params.endTime) {
    timeRange = [
      params.startTime,
      params.endTime,
    ];
  }

  // 支持*符号匹配的字段
  const symbolList: string[] = [
    BuglyFiltersName.Model,         // 机型
    BuglyFiltersName.AccountId,     // 用户ID
    BuglyFiltersName.DeviceId,      // 设备ID
    BuglyFiltersName.IssueUuid,     // Issue ID
    BuglyFiltersName.CrashId,       // 消息 ID
    BuglyFiltersName.Os,            // 系统版本
    BuglyFiltersName.ProcessName,   // 出错进程
    BuglyFiltersName.ThreadName,    // 出错线程
    BuglyFiltersName.ExceptionType,
    BuglyFiltersName.RomName,
    BuglyFiltersName.ChannelId,
  ];

  const getOperator = (name: string, value: string) => {
    const operator = getWildcardOperator(value);

    const getOperatorBySymbol = () => symbolList.reduce((obj, key) => ({ ...obj, [key]: operator }), {});

    const operatorSetting: Record<string, string> = {
      [BuglyFiltersName.StartTime]: 'GE',
      [BuglyFiltersName.EndTime]: 'LE',
      [BuglyFiltersName.StartTimeCompare]: 'GE',
      [BuglyFiltersName.EndTimeCompare]: 'LE',
      [BuglyFiltersName.AppVersion]: 'OR',
      [BuglyFiltersName.AppInBack]: FieldsOperator.OR,
      [BuglyFiltersName.SceneLabels]: FieldsOperator.OR,
      [BuglyFiltersName.files]: FieldsOperator.OR,
      [BuglyFiltersName.DeviceType]: FieldsOperator.OR,
      [BuglyFiltersName.Stack]: params?.stack?.mode,
      [BuglyFiltersName.OOMType]: FieldsOperator.OR,
      ...getOperatorBySymbol(),

      // 新UI
      [FieldKey.StandardModel]: params?.[FieldKey.StandardModel]?.mode,
      [FieldKey.StandardOsVersion]: params?.[FieldKey.StandardOsVersion]?.mode,
      [FieldKey.StandardSceneDataLabels]: params?.[FieldKey.StandardSceneDataLabels]?.mode,
    };

    return get(operatorSetting, name) || '';
  };

  const getType = (name: string) => {
    const operatorSetting: Record<string, any> = {
      [BuglyFiltersName.SceneLabels]: KeyDataType.ARRAY,
      [BuglyFiltersName.files]: KeyDataType.ARRAY,
    };

    return get(operatorSetting, name) || undefined;
  };

  const replaceSymbol = (param = '') => param?.replaceAll('*', '');

  const filtersCompare: Record<string, any> = {
    // [BuglyFiltersName.Stack]: params?.stack?.values,
    // [BuglyFiltersName.OOMType]: params?.oomType,
    [BuglyFiltersName.StartTime]: timeRange[0],
    [BuglyFiltersName.EndTime]: timeRange[1],
    [BuglyFiltersName.AppVersion]: params.version,
    // [BuglyFiltersName.AccountId]: params?.contectInfo ?? '',
    // [BuglyFiltersName.DeviceId]: params?.deviceId ?? '',
    // [BuglyFiltersName.AppInBack]: params?.appInBacks,
    // [BuglyFiltersName.BundleId]: params?.bundleId ?? '',
    // [BuglyFiltersName.ChannelId]: params?.channelId ?? '',
    // [BuglyFiltersName.CpuArc]: params?.cpuArch ?? '',
    // [BuglyFiltersName.IssueUuid]: params?.issueId ?? '',
    // [BuglyFiltersName.CrashId]: params?.crashId ?? '',
    // [BuglyFiltersName.Model]: params?.hardware ?? '',
    // [BuglyFiltersName.Os]: params?.osVersion ?? '',
    // [BuglyFiltersName.ProcessName]: params?.processName ?? '',
    // [BuglyFiltersName.ThreadName]: params?.threadName ?? '',
    // [BuglyFiltersName.CpuArchitecture]: params?.cpuArchitecture ?? '',
    // [BuglyFiltersName.SdkVersion]: params?.sdkVersion ?? '',
    // [BuglyFiltersName.RomName]: params?.rom ?? '',
    // [BuglyFiltersName.DeviceType]: params.deviceType ?? '',
    // [BuglyFiltersName.Platform]: params.platform ?? '',
    // [BuglyFiltersName.Linkages]: params.linkages ?? '',
    // // GB转B
    // [BuglyFiltersName.FreeMem]: params.freeMem ? {
    //   operator: params.freeMem?.operator,
    //   value: params.freeMem?.value?.map((val: number) => `${val * 1024 * 1024 * 1024}`),
    // } : '',
    // [BuglyFiltersName.LaunchDuration]: params.launchDuration ? {
    //   operator: params.launchDuration?.operator,
    //   value: params.launchDuration?.value?.map((val: number) => `${val}`),
    // } : '',
    // [BuglyFiltersName.ExceptionType]: params.errorType ?? '',
    // [BuglyFiltersName.Type]: params.exceptionTypeList ? getExceptionTypeIds(params.exceptionTypeList) : '',
    // [BuglyFiltersName.ShiplyTag]: params.shiplyTag ?? '',
    // [BuglyFiltersName.AbTestTag]: params.abTestTag ?? '',
    // [BuglyFiltersName.TagsInFilter]: params.tagsInFilter ?? [],
    // [BuglyFiltersName.SceneLabels]: params.sceneDataLabels ?? [],
    // [BuglyFiltersName.files]: params.attachFileNames ?? [],
    // [BuglyFiltersName.Stage]: params.stage ?? '',
    // [BuglyFiltersName.Brand]: params.brand ?? '',
    // // 新UI
    // [FieldKey.StandardAppVersion]: params?.[FieldKey.StandardAppVersion]?.values,
    // [FieldKey.StandardModel]: params?.[FieldKey.StandardModel]?.values,
    // [FieldKey.StandardOsVersion]: params?.[FieldKey.StandardOsVersion]?.values,
    // [FieldKey.StandardSceneDataLabels]: params?.[FieldKey.StandardSceneDataLabels]?.values,
    //
    // // 高级搜索用到
    // process_launch_id: params?.processLaunchId,
    // launch_id: params?.launchId,
  };

  const getName = (name: string) => {
    const isOccur = params.timeType === 'occur';
    const isOccurCompare = params.timeTypeCompare === 'occur';
    if (name === BuglyFiltersName.StartTime || name === BuglyFiltersName.EndTime) {
      return isOccur ? 'report_time' : 'event_time';
    }
    if (name === BuglyFiltersName.StartTimeCompare || name === BuglyFiltersName.EndTimeCompare) {
      return isOccurCompare ? 'report_time' : 'event_time';
    }
    // 处理新UI name
    switch (name) {
      case FieldKey.StandardModel:
        return BuglyFiltersName.Model;
      case FieldKey.StandardOsVersion:
        return BuglyFiltersName.Os;
      case FieldKey.StandardSceneDataLabels:
        return BuglyFiltersName.SceneLabels;
      default:
        return name;
    }
  };

  const newFilters = Object.keys(filtersCompare).map((key) => {
    // APP版本新UI处理
    if (key === FieldKey.StandardAppVersion) {
      return newAppVersionField2Filter(params);
    }

    if (key === BuglyFiltersName.AppVersion) {
      return appVersionField2Filter(params);
    }
    if (key === BuglyFiltersName.AppVersionCompare) {
      return appVersionField2Filter(params, false);
    }


    if (key === BuglyFiltersName.Linkages) {
      return {
        name: getName(key),
        values: filtersCompare[key],
        operator: FieldsOperator.OR,
        type: KeyDataType.ARRAY,
      };
    }

    // 实验ID和实验标签
    if (key === BuglyFiltersName.ShiplyTag || key === BuglyFiltersName.AbTestTag) {
      return {
        key: getFilterKeyForCustomTag(key as unknown as FieldKey),
        name: 'shiply_ab_test_tag',
        values: toArray(symbolList.includes(key) ? replaceSymbol(filtersCompare[key]) : filtersCompare[key]),
        operator: FieldsOperator.OR,
        type: KeyDataType.ARRAY,
      };
    }
    if (key === BuglyFiltersName.TagsInFilter) {
      return {
        key: 'a13',
        name: 'tags_in_filter',
        values: toArray(filtersCompare[key]),
        operator: FieldsOperator.OR,
        type: KeyDataType.ARRAY,
      };
    }

    if (key === BuglyFiltersName.LaunchDuration || key === BuglyFiltersName.FreeMem) {
      return {
        name: getName(key),
        operator: filtersCompare[key]?.operator,
        values: filtersCompare[key]?.value,
      };
    }
    return ({
      name: getName(key),
      values: toArray(symbolList.includes(key) ? replaceSymbol(filtersCompare[key]) : filtersCompare[key]),
      operator: getOperator(key, symbolList.includes(key) ? filtersCompare[key] : ''),
      type: getType(key),
    });
  });

  // 处理自定义字段filters
  const bizExtendInfo = params.bizExtendInfo?.map((bizExtend: IBizExtendInfo) => ({
    ...bizExtend,
    name: 'user_classify_data',
    operator: bizExtend.operator,
  })) ?? [];

  return newFilters.concat(bizExtendInfo)
    .filter(item => item && !item.values?.includes(FIELD_ALL) && item.values?.length > 0);
};

export const getCompareBuglyFilter = (params: any) => {
  const timeCostFieldValue = params?.standardLaunchDuration?.value ?? '';
  const timeCostValue = (!isEmpty(timeCostFieldValue) && timeCostFieldValue?.[0] && timeCostFieldValue?.[1])
    ? `${timeCostFieldValue?.[0]},${timeCostFieldValue?.[1]}` : `${timeCostFieldValue}`;

  const freeMemFieldValue = params?.standardFreeMem?.value ?? '';
  const singleFreeMemValue = isFinite(freeMemFieldValue?.[0]) && freeMemFieldValue !== '' ? freeMemFieldValue?.[0] * 1024 * 1024 * 1024 : freeMemFieldValue;
  const freeMemValue = (!isEmpty(freeMemFieldValue) && freeMemFieldValue?.[0] && freeMemFieldValue?.[1])
    ? `${freeMemFieldValue?.[0] * 1024 * 1024 * 1024},${freeMemFieldValue?.[1] * 1024 * 1024 * 1024}` : `${singleFreeMemValue}`;


  const exceptionTypeList = params.exceptionTypeList ? getExceptionTypeIds(params.exceptionTypeList) : '';
  const filters: {key?: string, name: string; values: string[]; operator: string;type?: number }[] = [
    {
      name: params.timeType === 'occur' ? 'report_time' : 'event_time',
      values: [`${moment(params.startDateStr).unix()}`],
      operator: 'GE',
    },
    {
      name: params.timeType === 'occur' ? 'report_time' : 'event_time',
      values: [`${moment(params.endDateStr).unix()}`],
      operator: 'LE',
    },
    newAppVersionField2Filter(params),
    {
      name: FieldKey.AccountId,
      values: toArray((params?.[FieldKey.StandardAccountId]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardAccountId]?.mode || MatchMode.OR,
    },
    {
      name: 'device_id',
      values: toArray((params?.[FieldKey.StandardQimei]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardQimei]?.mode || MatchMode.OR,
    },
    {
      name: 'qimei',
      values: toArray((params?.standardDeviceId?.values ?? '') as string),
      operator: params?.standardDeviceId?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.Stack,
      values: toArray((params?.standardStack?.values ?? '') as string),
      operator: params?.standardStack?.mode || MatchMode.MATCH,
    },
    {
      name: FieldKey.AppInBack,
      values: toArray(params?.standardAppInBack),
      operator: MatchMode.OR,
    },
    {
      name: 'launch_duration',
      values: toArray(timeCostValue),
      operator: params?.standardLaunchDuration?.operator,
    },
    {
      name: 'free_mem',
      values: toArray(freeMemValue),
      operator: params?.standardFreeMem?.operator,
    },
    {
      name: 'oom_type',
      values: toArray(params?.oomType),
      operator: MatchMode.OR,
    },
    {
      name: 'type',
      values: toArray(exceptionTypeList),
      operator: '',
    },
    {
      name: 'device_type',
      values: toArray(params?.deviceType),
      operator: MatchMode.OR,
    },
    {
      name: FieldKey.BundleId,
      values: toArray((params?.[FieldKey.StandardBundleId]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardBundleId]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.ChannelId,
      values: toArray((params?.standardChannelId?.values ?? '') as string),
      operator: params?.standardChannelId?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.Stage,
      values: toArray((params?.standardStage?.values ?? '') as string),
      operator: params?.standardStage?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.Brand,
      values: toArray((params?.[FieldKey.StandardBrand]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardBrand]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.ShiplyAbTestTag,
      key: getFilterKeyForCustomTag(FieldKey.ShiplyTag),
      values: toArray((params?.[FieldKey.StandardShiplyTag]?.values as string[]) ?? ''),
      operator: params?.[FieldKey.StandardShiplyTag]?.mode || MatchMode.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: FieldKey.ShiplyAbTestTag,
      key: getFilterKeyForCustomTag(FieldKey.AbTestTag),
      values: toArray((params?.[FieldKey.StandardAbTestTag]?.values as string[]) ?? ''),
      operator: params?.[FieldKey.StandardAbTestTag]?.mode || MatchMode.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: FieldKey.TagsInFilterUnderline,
      values: toArray((params?.[FieldKey.StandardTagsInFilter]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardTagsInFilter]?.mode || MatchMode.OR,
      key: 'a13',
      type: KeyDataType.ARRAY,
    },
    {
      name: 'cpu_arc',
      values: toArray((params?.standardCpuArch?.values ?? '') as string),
      operator: params?.standardCpuArch?.mode || MatchMode.OR,
    },
    {
      name: 'cpu_architecture',
      values: toArray((params?.standardCpuArchitecture?.values ?? '') as string),
      operator: params?.standardCpuArchitecture?.mode || MatchMode.OR,
    },
    {
      name: 'arch',
      values: toArray((params?.standardElectronCpuArch?.values ?? '') as string),
      operator: params?.standardElectronCpuArch?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.SdkVersion,
      values: toArray(params?.[FieldKey.StandardSdkVersion]?.values as string[]),
      operator: params?.[FieldKey.StandardSdkVersion]?.mode || MatchMode.OR,
    },
    {
      name: 'issue_uuid',
      values: toArray((params?.standardIssueId?.values ?? '') as string),
      operator: params?.standardIssueId?.mode || MatchMode.OR,
    },
    {
      name: 'crash_id',
      values: toArray((params?.standardCrashId?.values ?? '') as string),
      operator: params?.standardCrashId?.mode || MatchMode.OR,
    },
    {
      name: 'client_identify',
      values: toArray((params?.standardClientIdentify?.values ?? '') as string),
      operator: params?.standardClientIdentify?.mode || MatchMode.OR,
    },
    {
      name: 'exception_type',
      values: toArray((params?.standardErrorType?.values ?? '') as string),
      operator: params?.standardErrorType?.mode || MatchMode.OR,
    },
    {
      name: 'model',
      values: toArray((params?.standardHardware?.values ?? '') as string),
      operator: params?.standardHardware?.mode || MatchMode.OR,
    },
    {
      name: 'os',
      values: toArray((params?.[FieldKey.StandardOsVersion]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardOsVersion]?.mode || MatchMode.OR,
    },
    {
      name: 'process_name',
      values: toArray((params?.standardProcessName?.values ?? '') as string),
      operator: params?.standardProcessName?.mode || MatchMode.MATCH,
    },
    {
      name: 'pid',
      values: toArray((params?.standardProcessId?.values ?? '') as string),
      operator: params?.standardProcessId?.mode || MatchMode.MATCH,
    },
    {
      name: 'thread_name',
      values: toArray((params?.standardThreadName?.values ?? '') as string),
      operator: params?.standardThreadName?.mode || MatchMode.OR,
    },
    {
      name: 'rom_name',
      values: toArray((params?.standardRom?.values ?? '') as string),
      operator: params?.standardRom?.mode || MatchMode.OR,
    },
    {
      name: 'linkages',
      values: toArray((params?.standardLinkages?.values ?? '') as string),
      operator: params?.standardLinkages?.mode || MatchMode.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: 'scene_labels',
      values: toArray((params?.[FieldKey.StandardSceneDataLabels]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardSceneDataLabels]?.mode || MatchMode.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: 'files',
      values: toArray((params?.[FieldKey.StandardAttachFileNames]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardAttachFileNames]?.mode || MatchMode.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: 'main_stack',
      values: toArray((params?.standardMainStack?.values ?? '') as string),
      operator: params?.standardMainStack?.mode || MatchMode.OR,
    },
    {
      name: 'build_number',
      values: toArray((params?.standardBuildNumber?.values ?? '') as string),
      operator: params?.standardBuildNumber?.mode || MatchMode.OR,
    },
    {
      name: 'asan',
      values: toArray((params?.[FieldKey.StandardAsanType] ?? '') as string),
      operator: params?.[FieldKey.StandardAsanType]?.mode || MatchMode.OR,
    },
    {
      name: 'inferred_labels',
      values: toArray((params?.[FieldKey.StandardInferredLabels]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardInferredLabels]?.mode || MatchMode.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: FieldKey.ContinuousEvent,
      values: toArray((params?.standardContinuousEvent ?? '')),
      operator: MatchMode.OR,
    },
    {
      name: AreaNameEnum.ClientCountry,
      values: toArray(params?.[FieldKey.StandardArea]
        ?.find?.((item: { name: AreaNameEnum; }) => item.name === AreaNameEnum.ClientCountry)?.values),
      operator: FieldsOperator.OR,
    },
    {
      name: AreaNameEnum.ClientProvince,
      values: toArray(params?.[FieldKey.StandardArea]
        ?.find?.((item: { name: AreaNameEnum; }) => item.name === AreaNameEnum.ClientProvince)?.values),
      operator: FieldsOperator.OR,
    },
    {
      name: AreaNameEnum.ClientCity,
      values: toArray(params?.[FieldKey.StandardArea]
        ?.find?.((item: { name: AreaNameEnum; }) => item.name === AreaNameEnum.ClientCity)?.values),
      operator: FieldsOperator.OR,
    },
  ];

  // 处理自定义字段filters
  const bizExtendInfo = params.bizExtendInfo?.map((bizExtend: IBizExtendInfo) => ({
    ...bizExtend,
    name: 'user_classify_data',
    operator: bizExtend.operator,
  })) ?? [];

  // 处理自定义维度filters
  const userCustom = params.userCustom?.map((userCustom: IBizExtendInfo) => ({
    ...userCustom,
    name: 'user_custom',
    operator: userCustom.operator,
  })) ?? [];
  const filtersA = filters.concat(bizExtendInfo).concat(userCustom)
    .filter(item => isValidFilter(item));

  // filtersB
  const timeCostFieldValueB = params?.standardLaunchDurationCompare?.value ?? '';
  const timeCostValueB = (!isEmpty(timeCostFieldValueB) && timeCostFieldValueB?.[0] && timeCostFieldValueB?.[1])
    ? `${timeCostFieldValueB?.[0]},${timeCostFieldValueB?.[1]}` : `${timeCostFieldValueB}`;

  const freeMemFieldValueB = params?.standardFreeMemCompare?.value ?? '';
  const singleFreeMemValueB = isFinite(freeMemFieldValueB?.[0]) && freeMemFieldValueB !== '' ? freeMemFieldValueB?.[0] * 1024 * 1024 * 1024 : freeMemFieldValueB;
  const freeMemValueB = (!isEmpty(freeMemFieldValueB) && freeMemFieldValueB?.[0] && freeMemFieldValueB?.[1])
    ? `${freeMemFieldValueB?.[0] * 1024 * 1024 * 1024},${freeMemFieldValueB?.[1] * 1024 * 1024 * 1024}` : `${singleFreeMemValueB}`;


  const exceptionTypeListB = params.exceptionTypeListCompare ? getExceptionTypeIds(params.exceptionTypeListCompare) : '';
  const filterB: {key?: string, name: string; values: string[]; operator: string;type?: number }[] = [
    {
      name: params.timeTypeCompare === 'occur' ? 'report_time' : 'event_time',
      values: [`${moment(params.startDateStrCompare).unix()}`],
      operator: 'GE',
    },
    {
      name: params.timeTypeCompare === 'occur' ? 'report_time' : 'event_time',
      values: [`${moment(params.endDateStrCompare).unix()}`],
      operator: 'LE',
    },
    newAppVersionField2Filter(params, true, true),
    {
      name: FieldKey.AccountId,
      values: toArray((params?.[FieldKey.StandardAccountIdCompare]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardAccountIdCompare]?.mode || MatchMode.OR,
    },
    {
      name: 'device_id',
      values: toArray((params?.[FieldKey.StandardQimeiCompare]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardQimeiCompare]?.mode || MatchMode.OR,
    },
    {
      name: 'qimei',
      values: toArray((params?.standardDeviceIdCompare?.values ?? '') as string),
      operator: params?.standardDeviceIdCompare?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.Stack,
      values: toArray((params?.standardStackCompare?.values ?? '') as string),
      operator: params?.standardStackCompare?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.AppInBack,
      values: toArray(params?.standardAppInBackCompare),
      operator: MatchMode.OR,
    },
    {
      name: 'launch_duration',
      values: toArray(timeCostValueB),
      operator: params?.standardLaunchDurationCompare?.mode,
    },
    {
      name: 'free_mem',
      values: toArray(freeMemValueB),
      operator: params?.standardFreeMemCompare?.mode,
    },
    {
      name: 'oom_type',
      values: toArray(params?.oomTypeCompare),
      operator: MatchMode.OR,
    },
    {
      name: 'type',
      values: toArray(exceptionTypeListB),
      operator: '',
    },
    {
      name: 'device_type',
      values: toArray(params?.deviceTypeCompare),
      operator: MatchMode.OR,
    },
    {
      name: FieldKey.BundleId,
      values: toArray((params?.[FieldKey.StandardBundleIdCompare]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardBundleIdCompare]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.ChannelId,
      values: toArray((params?.standardChannelIdCompare?.values ?? '') as string),
      operator: params?.standardChannelIdCompare?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.Stage,
      values: toArray((params?.standardStageCompare?.values ?? '') as string),
      operator: params?.standardStageCompare?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.Brand,
      values: toArray((params?.[FieldKey.StandardBrandCompare]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardBrandCompare]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.ShiplyAbTestTag,
      key: getFilterKeyForCustomTag(FieldKey.ShiplyTag),
      values: toArray((params?.[FieldKey.StandardShiplyTagCompare]?.values as string[]) ?? ''),
      operator: params?.[FieldKey.StandardShiplyTagCompare]?.mode || MatchMode.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: FieldKey.ShiplyAbTestTag,
      key: getFilterKeyForCustomTag(FieldKey.AbTestTag),
      values: toArray((params?.[FieldKey.StandardAbTestTagCompare]?.values as string[]) ?? ''),
      operator: params?.[FieldKey.StandardAbTestTagCompare]?.mode || MatchMode.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: 'cpu_arc',
      values: toArray((params?.standardCpuArchCompare?.values ?? '') as string),
      operator: params?.standardCpuArchCompare?.mode || MatchMode.OR,
    },
    {
      name: 'cpu_architecture',
      values: toArray((params?.standardCpuArchitectureCompare?.values ?? '') as string),
      operator: params?.standardCpuArchitectureCompare?.mode || MatchMode.OR,
    },
    {
      name: 'arch',
      values: toArray((params?.standardElectronCpuArchCompare?.values ?? '') as string),
      operator: params?.standardElectronCpuArchCompare?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.SdkVersion,
      values: toArray(params?.[FieldKey.StandardSdkVersionCompare]?.values as string[]),
      operator: params?.[FieldKey.StandardSdkVersionCompare]?.mode || MatchMode.OR,
    },
    {
      name: 'issue_uuid',
      values: toArray((params?.standardIssueIdCompare?.values ?? '') as string),
      operator: params?.standardIssueIdCompare?.mode || MatchMode.OR,
    },
    {
      name: 'crash_id',
      values: toArray((params?.standardCrashIdCompare?.values ?? '') as string),
      operator: params?.standardCrashIdCompare?.mode || MatchMode.OR,
    },
    {
      name: 'client_identify',
      values: toArray((params?.standardClientIdentifyCompare?.values ?? '') as string),
      operator: params?.standardClientIdentifyCompare?.mode || MatchMode.OR,
    },
    {
      name: 'exception_type',
      values: toArray((params?.standardErrorTypeCompare?.values ?? '') as string),
      operator: params?.standardErrorTypeCompare?.mode || MatchMode.OR,
    },
    {
      name: 'model',
      values: toArray((params?.standardHardwareCompare?.values ?? '') as string),
      operator: params?.standardHardwareCompare?.mode || MatchMode.OR,
    },
    {
      name: 'os',
      values: toArray((params?.[FieldKey.StandardOsVersionCompare]?.values ?? '') as string),
      operator: params?.[FieldKey.StandardOsVersionCompare]?.mode || MatchMode.OR,
    },
    {
      name: 'process_name',
      values: toArray((params?.standardProcessNameCompare?.values ?? '') as string),
      operator: params?.standardProcessNameCompare?.mode || MatchMode.MATCH,
    },
    {
      name: 'pid',
      values: toArray((params?.standardProcessIdCompare?.values ?? '') as string),
      operator: params?.standardProcessIdCompare?.mode || MatchMode.MATCH,
    },
    {
      name: 'thread_name',
      values: toArray((params?.standardThreadNameCompare?.values ?? '') as string),
      operator: params?.standardThreadNameCompare?.mode || MatchMode.MATCH,
    },
    {
      name: 'rom_name',
      values: toArray((params?.standardRomCompare?.values ?? '') as string),
      operator: params?.standardRomCompare?.mode || MatchMode.OR,
    },
    {
      name: 'linkages',
      values: toArray((params?.linkagesCompare) as string),
      operator: MatchMode.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: 'scene_labels',
      values: toArray((params?.sceneDataLabelsCompare) as string),
      operator: MatchMode.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: 'main_stack',
      values: toArray((params?.standardMainStackCompare?.values ?? '') as string),
      operator: params?.standardMainStackCompare?.mode || MatchMode.OR,
    },
    {
      name: 'build_number',
      values: toArray((params?.standardBuildNumberCompare?.values ?? '') as string),
      operator: params?.standardBuildNumberCompare?.mode || MatchMode.OR,
    },
    {
      name: 'asan',
      values: toArray((params?.[FieldKey.StandardAsanType] ?? '') as string),
      operator: params?.[FieldKey.StandardAsanTypeCompare]?.mode || MatchMode.OR,
    },
    {
      name: FieldKey.TagsInFilterUnderline,
      values: toArray((params?.standardTagsInFilterCompare?.values ?? '') as string),
      operator: params?.standardTagsInFilterCompare?.mode || MatchMode.OR,
      key: 'a13',
      type: KeyDataType.ARRAY,
    },
    {
      name: 'inferred_labels',
      values: toArray((params?.standardInferredLabelsCompare?.values ?? '') as string),
      operator: params?.standardInferredLabelsCompare?.mode || MatchMode.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: 'linkages',
      values: toArray((params?.standardLinkagesCompare?.values ?? '') as string),
      operator: params?.standardLinkagesCompare?.mode || MatchMode.OR,
      type: KeyDataType.ARRAY,
    },
    {
      name: 'oom_type',
      values: toArray(params?.oomTypeCompare),
      operator: MatchMode.OR,
    },
    {
      name: FieldKey.ContinuousEvent,
      values: toArray((params?.standardContinuousEventCompare ?? '')),
      operator: MatchMode.OR,
    },
    {
      name: AreaNameEnum.ClientCountry,
      values: toArray(params?.standardAreaCompare
        ?.find?.((item: { name: AreaNameEnum; }) => item.name === AreaNameEnum.ClientCountry)?.values),
      operator: FieldsOperator.OR,
    },
    {
      name: AreaNameEnum.ClientProvince,
      values: toArray(params?.standardAreaCompare
        ?.find?.((item: { name: AreaNameEnum; }) => item.name === AreaNameEnum.ClientProvince)?.values),
      operator: FieldsOperator.OR,
    },
    {
      name: AreaNameEnum.ClientCity,
      values: toArray(params?.standardAreaCompare
        ?.find?.((item: { name: AreaNameEnum; }) => item.name === AreaNameEnum.ClientCity)?.values),
      operator: FieldsOperator.OR,
    },
    {
      name: 'files',
      values: toArray((params?.standardAttachFileNamesCompare?.values ?? '') as string),
      operator: params?.standardAttachFileNamesCompare?.mode || MatchMode.OR,
      type: KeyDataType.ARRAY,
    },
  ];

  // 处理自定义字段filters
  const bizExtendInfoB = params.bizExtendInfoCompare?.map((bizExtend: IBizExtendInfo) => ({
    ...bizExtend,
    name: 'user_classify_data',
    operator: bizExtend.operator,
  })) ?? [];

  // 处理自定义维度filters
  const userCustomB = params.userCustomCompare?.map((userCustom: IBizExtendInfo) => ({
    ...userCustom,
    name: 'user_custom',
    operator: userCustom.operator,
  })) ?? [];

  const filtersB = filterB.concat(bizExtendInfoB).concat(userCustomB)
    .filter(item => isValidFilter(item));

  return [
    filtersA,
    filtersB,
  ];
};

// 自定义时间范围产品id
enum CustomTimeRangeProductId {
  CfIos = 'i1104512706',      // cf ios
  Af = '1104512706',          // AF
  CfAndroid = '1105019390',   // cf android
}

// 根据产品id获取最大可选择范围天数
export const getLimitDays = (issueType: IssueType) => {
  const invertObj = invert(CustomTimeRangeProductId);
  const limitDaysSetting = mapValues(invertObj, () => (issueType === IssueType.ISSUE_TYPE_CRASH
    ? 90 : DEFAULT_BUGLY_LIMIT_DAYS));
  const limitDays = get(limitDaysSetting, getProductId());

  return limitDays || DEFAULT_BUGLY_LIMIT_DAYS;
};

const DEFAULT_RANGE = {
  ['近1小时']: CommonDateRange.hour(),
  ['今天']: CommonDateRange.today(),
  ['昨天']: CommonDateRange.yesterday(),
  ['近1天']: CommonDateRange.lastDays(1),
  ['近2天']: CommonDateRange.lastDays(2),
  ['近7天']: CommonDateRange.lastDays(7),
  ['近15天']: CommonDateRange.lastDays(15),
  ['近30天']: CommonDateRange.lastDays(30),
};

// 根据产品id获取可选择的快捷标签
export const getRange = (issueType: IssueType) => {
  const invertObj = invert(CustomTimeRangeProductId);
  const rangeSetting = mapValues(invertObj, () => (issueType === IssueType.ISSUE_TYPE_CRASH
    ? {
      ...pickBy(DEFAULT_RANGE, (_, key) => key !== '近2天'),
      ['近90天']: CommonDateRange.lastDays(90),
    }
    : DEFAULT_RANGE));
  const range = get(rangeSetting, getProductId());

  return range || DEFAULT_RANGE;
};

const defaultTimeQuickOptions = [
  {
    label: i18n.t('自定义'),
    value: 'custom',
  },
  {
    label: i18n.t('近1小时'),
    value: '近1小时',
  },
  {
    label: i18n.t('今天'),
    value: '今天',
  },
  {
    label: i18n.t('昨天'),
    value: '昨天',
  },
  {
    label: i18n.t('近7天'),
    value: '近7天',
  },
  {
    label: i18n.t('近30天'),
    value: '近30天',
  },
  {
    label: i18n.t('近45天'),
    value: '近45天',
  },
  {
    label: i18n.t('近90天'),
    value: '近90天',
  },
];

// 根据产品id获取可选择的时间快捷选项
export const getTimeQuickOptions = (
  issueType: number,
  showTime: boolean,
) => {
  const invertObj = invert(CustomTimeRangeProductId);
  const timeQuickSetting = mapValues(invertObj, () => (issueType === IssueType.ISSUE_TYPE_CRASH
    ? [
      {
        label: i18n.t('自定义'),
        value: 'custom',
      },
      {
        label: i18n.t('近1小时'),
        value: '近1小时',
      },
      {
        label: i18n.t('今天'),
        value: '今天',
      },
      {
        label: i18n.t('昨天'),
        value: '昨天',
      },
      {
        label: i18n.t('近7天'),
        value: '近7天',
      },
      {
        label: i18n.t('近30天'),
        value: '近30天',
      },
      {
        label: i18n.t('近90天'),
        value: '近90天',
      },
    ] : defaultTimeQuickOptions));
  const timeQuickHour = '近1小时';
  const timeQuick = get(timeQuickSetting, getProductId());
  const range = showTime
    ? timeQuick
    : timeQuick?.filter(item => item.value !== timeQuickHour);
  let timeQuickOptions;
  if (issueType === IssueType.ISSUE_TYPE_TRAFFIC && showTime) {
    timeQuickOptions = [
      {
        label: i18n.t('自定义'),
        value: 'custom',
      },
      {
        label: i18n.t('近1小时'),
        value: '近1小时',
      },
      {
        label: i18n.t('今天'),
        value: '今天',
      },
      {
        label: i18n.t('昨天'),
        value: '昨天',
      },
      {
        label: i18n.t('近7天'),
        value: '近7天',
      },
    ];
  } else if (showTime) {
    timeQuickOptions = defaultTimeQuickOptions;
  } else {
    timeQuickOptions = defaultTimeQuickOptions.filter(item => item.value !== timeQuickHour);
  }

  return range || timeQuickOptions;
};


export const getTableTimeQuickOptions = (
  issueType: IssueType,
  showTime: boolean,
  timeWindow: TimeType,
) => {
  const invertObj = invert(CustomTimeRangeProductId);
  const timeQuickSettingOptions = mapValues(invertObj, () => {
    if (issueType === IssueType.ISSUE_TYPE_CRASH) {
      return timeQuickSetting;
    }
    return timeQuickSetting;
  });

  const timeQuickHour = '近1小时';
  const timeQuick = get(timeQuickSettingOptions, getProductId());
  if (!timeQuick || issueType !== IssueType.ISSUE_TYPE_CRASH) {
    timeQuickSetting[TimeType.DAY][3] = {
      label: i18n.t('近45天'),
      value: '近45天',
    };
    timeQuickSetting[TimeType.DAY][4] = {
      label: i18n.t('近90天'),
      value: '近90天',
    };
  }
  const range = showTime
    ? timeQuick?.[timeWindow]
    : timeQuick[timeWindow]?.filter((item: { value: string; }) => item.value !== timeQuickHour);
  const timeQuickOptions = showTime
    ? timeQuickSetting?.[timeWindow]
    : timeQuickSetting?.[timeWindow].filter((item: { value: string; }) => item.value !== timeQuickHour);
  return range || timeQuickOptions;
};

export const reportFiltersEditSearchCriteria = (
  filters: IFilter[],
  pgId: string,
) => {
  filters?.forEach((filter) => {
    reportClick(pgId, CUSTOM_ACTIONS.commonEditSearchCriteria, {
      from: pgId,
      param3: filter.name ?? '',
      param4: filter.values?.join(',') ?? '',
    });
  });
};

export const reportEditSearchCriteria = (
  values: Record<string, any>,
  pgId: string,
) => {
  const filterValues = pickBy(values, (val, key) => {
    // 判断前后台的默认值'0'
    const defaultValue = key === 'appInBacks' && val === '0' ? '' : val;
    const filterValue = isArray(val) ? val.filter(item => !!item) : defaultValue;
    // isEmpty会判断boolean和number为true
    return !isEmpty(filterValue) || isBoolean(val) || isNumber(val);
  });
  if (!isEmpty(filterValues)) {
    Object.keys(filterValues)?.forEach((key) => {
      const label = get(REPORT_ISSUE_FORM_LABEL, key) || get(REPORT_ISSUE_FORM_LABEL, toHumpString(key));
      if (label) {
        reportClick(pgId, CUSTOM_ACTIONS.commonEditSearchCriteria, {
          from: pgId,
          param3: label,
          param4: filterValues[key],
        });
      }
    });
  }
};


export const TABLE_DEVICE_TYPE_OPTIONS = [
  { label: i18n.t('所有'), value: FIELD_ALL },
  { label: i18n.t('真机+模拟器'), value: DeviceType.All },
  { label: i18n.t('真机'), value: DeviceType.Device },
  { label: i18n.t('模拟器'), value: DeviceType.Simulator },
];
